import React from "react";

import { makeStyles } from "@material-ui/styles";

import { Drawer, Divider, ListItem, ListItemText, List as MuiList } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import { translate } from "jazasoft";

import { isEmpty } from "../../../utils/helpers";
import { useSetting } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";

const getUpdateText = (field, fromValue, toValue) => {
  return (
    <div>
      <span style={{ fontWeight: "bold" }}>{field}</span> changed from '
      <span style={{ fontStyle: "italic", textDecoration: "underline" }}>{fromValue}</span> ' to '
      <span style={{ fontStyle: "italic", textDecoration: "underline" }}>{toValue}</span> '
    </div>
  );
};

const getSbUpdateText = (field, sb, fromValue, toValue) => {
  let list = [];
  if (!isEmpty(sb.sizeGroup)) {
    list.push(sb.sizeGroup);
  }
  if (!isEmpty(sb.size)) {
    list.push(sb.size);
  }
  return (
    <div>
      <span style={{ fontWeight: "bold" }}>{list.join(" -> ")}</span> = <span style={{ fontWeight: "bold" }}>{field}</span> changed from '
      <span style={{ fontStyle: "italic", textDecoration: "underline" }}>{fromValue}</span> ' to '
      <span style={{ fontStyle: "italic", textDecoration: "underline" }}>{toValue}</span> '
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flex: 1,
    flexDirection: "column"
  },
  drawerHeader: {
    display: "flex",
    width: props => props.drawerWidth || 500,
    alignItems: "center",
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  content: {
    flex: 1
  }
}));

const OrderActivityDetailsDrawer = ({ open, prevObject, currObject, sbList, resource, onDrawerClose, translate }) => {
  const classes = useStyles({
    drawerWidth: 500
  });

  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, "orders");
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, "flowInfo");
  const partFields = ["id", "partName", "placement", "bomCu", "uom", "bomFabColor", "fabOrderQty", "bomItemCode"];
  const sbFields = ["id", "size", "sizeGroup", "qty", "serialNo"];

  return (
    <Drawer anchor="right" open={open} onClose={onDrawerClose} style={{ flex: 1 }}>
      <div className={classes.root}>
        {/** Drawer Header */}
        <div className={classes.drawerHeader}>
          <div style={{ padding: "0.5rem" }}>
            <IconButton onClick={onDrawerClose}>
              <ChevronRightIcon />
            </IconButton>
          </div>
        </div>
        <Divider />

        {/** Drawer Content **/}
        <div className={classes.content}>
          {resource === "Order" && (
            <MuiList disablePadding>
              {["id", ...orderFields, "desc"]
                ?.filter(field => Object.keys(currObject).includes(field) && !isEmpty(currObject[field]) && currObject[field] !== prevObject[field])
                ?.map((field, idx) => {
                  const translatedText = `${translate(`resources.orders.fields.${field}`, { _: field })}`;
                  const styledText = getUpdateText(translatedText, prevObject[field], currObject[field]);
                  return (
                    <ListItem key={idx} divider>
                      <ListItemText primary={styledText} />
                    </ListItem>
                  );
                })}
              {["id", ...orderFields, "desc"]?.filter(
                field => Object.keys(currObject).includes(field) && !isEmpty(currObject[field]) && currObject[field] !== prevObject[field]
              )?.length === 0 && (
                <ListItem divider>
                  <ListItemText primary={"No changes in Order Summary"} />
                </ListItem>
              )}
            </MuiList>
          )}
          {resource === "FlowInfo" && (
            <MuiList disablePadding>
              {flowInfoFields
                ?.filter(field => currObject[field] !== prevObject[field])
                ?.map((field, idx) => {
                  const translatedText = `${translate(`resources.orders.fields.${field}`, { _: field })}`;
                  const styledText = getUpdateText(translatedText, prevObject[field], currObject[field]);
                  return (
                    <ListItem key={idx} divider>
                      <ListItemText primary={styledText} />
                    </ListItem>
                  );
                })}
              {flowInfoFields?.filter(field => currObject[field] !== prevObject[field])?.length === 0 && (
                <ListItem divider>
                  <ListItemText primary={"No changes in Order Details"} />
                </ListItem>
              )}
            </MuiList>
          )}
          {resource === "Part" && (
            <MuiList disablePadding>
              {partFields
                ?.filter(field => currObject[field] !== prevObject[field])
                ?.map((field, idx) => {
                  const translatedText = `${translate(`resources.orders.part.fields.${field}`, { _: field })}`;
                  const styledText = getUpdateText(translatedText, prevObject[field], currObject[field]);
                  return (
                    <ListItem key={idx} divider>
                      <ListItemText primary={styledText} />
                    </ListItem>
                  );
                })}
              {partFields?.filter(field => currObject[field] !== prevObject[field])?.length === 0 && (
                <ListItem divider>
                  <ListItemText primary={"No changes in Part Details"} />
                </ListItem>
              )}
            </MuiList>
          )}
          {resource === "SizeBreakup" && (
            <MuiList disablePadding>
              {isEmpty(sbList) ? (
                <ListItem divider>
                  <ListItemText primary={"No changes in Size Breakup Details"} />
                </ListItem>
              ) : (
                sbList.map(sb => {
                  return (
                    <>
                      {sbFields
                        ?.filter(field => sb?.currObject[field] !== sb?.prevObject[field])
                        ?.map((field, idx) => {
                          const translatedText = `${translate(`resources.orders.sizeBreakup.fields.${field}`, { _: field })}`;
                          const styledText = getSbUpdateText(translatedText, sb, sb?.prevObject[field], sb?.currObject[field]);
                          return (
                            <ListItem key={idx} divider>
                              <ListItemText primary={styledText} />
                            </ListItem>
                          );
                        })}
                      {sbFields?.filter(field => sb?.currObject[field] !== sb?.prevObject[field])?.length === 0 && (
                        <ListItem divider>
                          <ListItemText primary={"No changes in Size Breakup Details"} />
                        </ListItem>
                      )}
                    </>
                  );
                })
              )}
            </MuiList>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default translate(OrderActivityDetailsDrawer);
